import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/data";

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (ready && user) nav("/admin/dashboard");
  }, [ready, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-[#143D63] flex items-center justify-center px-5 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-[#F9F8F6] p-10"
      >
        <Link to="/" className="inline-block mb-8">
          <img src={BRAND.logo} alt={BRAND.full} className="h-14 w-auto" />
        </Link>
        <p className="overline text-[#8B0000] mb-2 flex items-center gap-2"><Lock size={13} /> Studio access</p>
        <h1 className="font-serif text-4xl text-[#143D63] mb-8">Admin Login</h1>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="overline text-[#143D63]/60 mb-2 block">Email</label>
            <input
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none"
              placeholder="admin@thezebo.com"
            />
          </div>
          <div>
            <label className="overline text-[#143D63]/60 mb-2 block">Password</label>
            <input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-[#143D63]/20 bg-white px-4 py-3 text-sm focus:border-[#F8C21C] focus:outline-none"
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm tracking-wide hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Signing in…</> : "Sign In"}
          </button>
        </form>
      </motion.div>
    </main>
  );
}
